import { addMonths, addYears, differenceInCalendarMonths, format, parseISO } from 'date-fns'
import Decimal from 'decimal.js'

import { GerenciadorIntermediariasBase } from '@/domain/simulador/gerenciador-intermediarias-base'
import { PeriodicidadePagamentoEnum } from '@/types/simulacao'
import { ProcessadorValoresDecimal } from '@/utils/calculadora-valores-totais'
import type {
  TGrupoIntermediarias,
  TGrupoMensais,
} from '@/utils/calculadora-valores-totais/processador-valores-decimal'
import { dateToUTC } from '@/utils/date.utils'

export class GerenciadorIntermediariasPosConclusaoSacocVParcelado extends GerenciadorIntermediariasBase {
  private calcularPrimeiraData(
    dataPrimeiraPosConclusao: string,
    previsaoEntrega: string,
    dataUltimaIntermediariaCondicaoPreco?: string,
  ): string {
    const isEntregue = new Date() > new Date(previsaoEntrega)

    const intermediariaHaMaisDe12Meses = dataUltimaIntermediariaCondicaoPreco
      ? differenceInCalendarMonths(new Date(dataUltimaIntermediariaCondicaoPreco), new Date(previsaoEntrega)) > 12
      : false

    if (!isEntregue) {
      if (intermediariaHaMaisDe12Meses || !dataUltimaIntermediariaCondicaoPreco) {
        return format(new Date(dateToUTC(previsaoEntrega)), 'yyyy-MM-dd')
      }

      const dataInicio = addYears(new Date(dateToUTC(dataUltimaIntermediariaCondicaoPreco)), 1)
      return format(dataInicio, 'yyyy-MM-dd')
    }
    return format(new Date(dateToUTC(dataPrimeiraPosConclusao)), 'yyyy-MM-dd')
  }

  datasIniciais(
    dataPrimeiraPosConclusao: string,
    previsaoEntrega: string,
    quantidadeParcelas: number,
    dataUltimaIntermediariaCondicaoPreco?: string,
  ): string[] {
    const datas: string[] = []

    const primeiraData = this.calcularPrimeiraData(
      dataPrimeiraPosConclusao,
      previsaoEntrega,
      dataUltimaIntermediariaCondicaoPreco,
    )
    datas.push(primeiraData)

    for (let i = 1; i < quantidadeParcelas; i++) {
      const dataAnterior = new Date(dateToUTC(datas[i - 1]!))
      const proximaData = addYears(dataAnterior, 1)
      datas.push(format(proximaData, 'yyyy-MM-dd'))
    }

    return datas
  }

  calcularQuantidadeParcelasInicial(quantidadeGrupo: number) {
    if (quantidadeGrupo >= 24) return 2
    return 1
  }

  calcularValorEQuantidadeParcelas(
    valorSaldo: number,
    grupoPosConclusaoFormatoSaldo: TGrupoMensais | null,
    grupoPosConclusaoFormatoParcelado: TGrupoMensais,
  ) {
    const quantidadeParcelas = this.calcularQuantidadeParcelasInicial(grupoPosConclusaoFormatoParcelado.quantidade)

    const processadorValorGrupos = new ProcessadorValoresDecimal()
    const valorPosConclusaoFormatoSaldo =
      processadorValorGrupos.calcularValorPosConclusao(grupoPosConclusaoFormatoSaldo)
    const valorPosConclusaoFormatoParcelado = processadorValorGrupos.calcularValorPosConclusao(
      grupoPosConclusaoFormatoParcelado,
    )

    return {
      valor: new Decimal(valorSaldo)
        .plus(valorPosConclusaoFormatoSaldo)
        .minus(valorPosConclusaoFormatoParcelado)
        .dividedBy(quantidadeParcelas)
        .toNumber(),
      quantidade: quantidadeParcelas,
    }
  }

  criarGrupoInicialCompleto(
    valorSaldo: number,
    grupoPosConclusaoFormatoSaldo: TGrupoMensais | null,
    grupoPosConclusaoFormatoParcelado: TGrupoMensais,
    previsaoEntrega: string,
    dataUltimaIntermediariaParametro?: string,
  ): TGrupoIntermediarias[] {
    const { valor, quantidade } = this.calcularValorEQuantidadeParcelas(
      valorSaldo,
      grupoPosConclusaoFormatoSaldo,
      grupoPosConclusaoFormatoParcelado,
    )

    const datas = this.datasIniciais(
      grupoPosConclusaoFormatoParcelado.dataInicio!,
      previsaoEntrega,
      quantidade,
      dataUltimaIntermediariaParametro,
    )

    return this.criarGrupoInicial(datas, valor, undefined, 'Anual')
  }

  criarNovoGrupo(
    dataPrevisaoDeEntrega: string,
    dataPrimeiraMensalPosConclusao: string | null = null,
    dataUltimaParcela: string | null = null,
  ) {
    const isEntregue = new Date() > new Date(dateToUTC(dataPrevisaoDeEntrega))
    if (!dataUltimaParcela) {
      if (!isEntregue) {
        return {
          valor: 0,
          periodicidade: PeriodicidadePagamentoEnum.SemPeriodoFixo,
          parcelas: [{ data: dataPrevisaoDeEntrega }],
        }
      }

      return {
        valor: 0,
        periodicidade: PeriodicidadePagamentoEnum.SemPeriodoFixo,
        parcelas: [{ data: dataPrimeiraMensalPosConclusao }],
      }
    }

    const proximaData = format(addMonths(parseISO(dataUltimaParcela), 1), 'yyyy-MM-dd')
    return {
      valor: 0,
      periodicidade: PeriodicidadePagamentoEnum.SemPeriodoFixo,
      parcelas: [{ data: proximaData }],
    }
  }
}
